import React, { useEffect, useRef, useState } from 'react';

/**
 * ParallaxHero - A reusable hero section with subtle parallax scrolling effect
 * 
 * Props:
 * - children: Content to render inside the hero
 * - backgroundColor: Background color (default: primaryTeal)
 * - intensity: Parallax intensity 0-1 (default: 0.3)
 * - minHeight: Minimum height of the hero section
 * - className: Additional CSS classes
 * - overlayOpacity: Opacity of the gradient overlay (default: 0.3)
 */
const ParallaxHero = ({ 
  children, 
  backgroundColor = '#3D6B67',
  intensity = 0.3,
  minHeight = 'auto',
  className = '',
  overlayOpacity = 0.3,
  style = {}
}) => {
  const heroRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      
      const rect = heroRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Only apply parallax when hero is in view
      if (rect.bottom > 0 && rect.top < windowHeight) {
        const scrolled = window.scrollY;
        const parallaxOffset = scrolled * intensity;
        setOffset(parallaxOffset);
      }
    };

    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 100);

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [intensity]);

  return (
    <section 
      ref={heroRef}
      className={`relative overflow-hidden ${className}`}
      style={{ 
        backgroundColor,
        minHeight,
        ...style
      }}
    >
      {/* Parallax Background Layer */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          transform: `translateY(${offset}px)`,
          willChange: 'transform',
        }}
      >
        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: `linear-gradient(135deg, rgba(0,0,0,${overlayOpacity}) 0%, transparent 50%, rgba(0,0,0,${overlayOpacity * 0.5}) 100%)` 
          }} 
        />
        
        {/* Subtle pattern/texture effect */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
                              radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 0%, transparent 50%)`,
          }}
        />
      </div>

      {/* Floating decorative elements */}
      <div 
        className="absolute inset-0 pointer-events-none overflow-hidden"
        style={{ transform: `translateY(${offset * 0.5}px)` }}
      >
        <div 
          className="absolute top-20 right-10 w-64 h-64 rounded-full opacity-10"
          style={{ 
            background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
            transform: `translateY(${-offset * 0.3}px)`,
          }}
        />
        <div 
          className="absolute bottom-10 left-10 w-48 h-48 rounded-full opacity-5"
          style={{ 
            background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)',
            transform: `translateY(${-offset * 0.2}px)`,
          }}
        />
      </div>

      {/* Content Layer */}
      <div 
        className={`relative z-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {children}
      </div>
    </section>
  );
};

export default ParallaxHero;
